import { gql, NetworkStatus, useQuery } from "@apollo/client";
import debounce from "lodash/debounce";
import { useCallback, useState } from "react";

import { HISTORY_FILTER_LIMIT } from "@zapier/reporting/src/constants";
import { useSwrQuery } from "@zapier/reporting/src/hooks";
import {
  AccountMemberQuery,
  AccountMembersQuery,
  MemberFragment as Member,
} from "./useMembers.generated";

const memberFragment = gql`
  fragment Member on Member {
    email
    id
    imageUrl
    name
    recordId
  }
`;

export const getSelectedMembers = gql`
  ${memberFragment}

  query AccountMember($accountId: ID!, $memberIds: [ID!]!) {
    account(id: $accountId) {
      id
      membersById(ids: $memberIds) {
        edges {
          ...Member
        }
      }
    }
  }
`;

export const getUserFilter = gql`
  ${memberFragment}

  query AccountMembers(
    $accountId: ID!
    $limit: Int
    $offset: Int
    $search: String
    $userId: ID
  ) {
    account(id: $accountId) {
      id
      currentUser: member(id: $userId) {
        ...Member
      }
      members(limit: $limit, offset: $offset, search: $search) {
        edges {
          ...Member
        }
        totalCount
      }
    }
  }
`;

type Input = {
  accountId?: string;
  /** Pass in the ID of the member preselected, if present */
  memberIds: string[] | undefined;
  // current user id
  userId: string | undefined;
};

export function useMembers({ accountId, memberIds = [], userId }: Input) {
  const [search, setSearch] = useState<string>("");

  const getUserFilterRes = useSwrQuery<AccountMembersQuery>(getUserFilter, {
    notifyOnNetworkStatusChange: true,
    skip: !accountId,
    variables: {
      accountId,
      limit: HISTORY_FILTER_LIMIT,
      offset: 0,
      search,
      userId,
    },
  });
  const {
    client,
    data,
    error,
    loading: isUserLoading,
    fetchMore,
    networkStatus,
  } = getUserFilterRes;

  const selectedMembersResult = useQuery<AccountMemberQuery>(
    getSelectedMembers,
    {
      skip: !accountId || memberIds.length === 0,
      variables: {
        accountId,
        memberIds,
      },
    }
  );

  function getSelectedMembersCache() {
    const serializedCache: Record<string, any> = client.cache.extract();
    return Object.values(serializedCache).filter((item) => {
      return (
        item.__typename === "Member" &&
        memberIds.includes(String(item.recordId))
      );
    }) as Member[];
  }

  const currentUser = data?.account?.currentUser;
  const unparsedMembers = data?.account?.members?.edges || [];
  const selectedMembers = selectedMembersResult.loading
    ? getSelectedMembersCache()
    : selectedMembersResult.data?.account.membersById.edges || [];
  // Grandfathered in on 26-08-2024, OB-1373
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSearch = useCallback(debounce(setSearch, 500), []);

  const showCurrentUserFirst =
    search.length === 0 ||
    currentUser?.name?.toLowerCase().includes(search.toLowerCase());

  const members = [
    !isUserLoading && showCurrentUserFirst
      ? { ...currentUser, name: currentUser?.name }
      : undefined,
    ...unparsedMembers
      .filter((m: Member) => m.recordId !== currentUser?.recordId)
      .sort((a: Member, b: Member) => {
        return (a.name || a.email).localeCompare(b.name || b.email);
      })
      .filter(Boolean),
  ].filter(Boolean) as Member[];

  function onLoadMore() {
    fetchMore({
      //@ts-ignore ts is having trouble with the return types here for some reason
      updateQuery(prev, { fetchMoreResult }) {
        // We didn't get anything so nothing to update
        if (!fetchMoreResult) return prev;

        // Update the cache
        return {
          ...fetchMoreResult,
          account: {
            ...fetchMoreResult.account,
            members: {
              ...fetchMoreResult.account?.members,
              edges: [
                ...(prev.account?.members?.edges || []),
                ...(fetchMoreResult.account?.members?.edges || []),
              ],
            },
          },
        };
      },
      variables: {
        accountId,
        limit: HISTORY_FILTER_LIMIT,
        offset: unparsedMembers.length,
        search,
        userId,
      },
    });
  }

  return {
    error,
    isLoading: isUserLoading,
    isLoadingMore: networkStatus === NetworkStatus.fetchMore,
    members,
    onLoadMore:
      unparsedMembers.length < (data?.account?.members?.totalCount || 0)
        ? onLoadMore
        : undefined,
    onSearch,
    search,
    selectedMembers,
  };
}
